import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useStoreLocatorContext } from './context';
import { Alert, Button, Icon, IconSvg, Input, Spinner } from '@/components';
import classNames from 'classnames';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { debounce } from 'lodash';

import 'swiper/css';

import { TreezAndExternalStore } from 'utils/utils';

import {
  buildPickupFilterTabs,
  filterStoresByPickupTab,
  PICKUP_FILTER_TAB_ALL_ID,
  PickupFilterTabModel,
} from '@/utils';
import { CURRENT_LOCATION_LABEL } from './constants';
import { StoresList } from './StoresList.components';
import styles from './storelocator.module.scss';

// Swiper’s default list includes `button`; that blocks mouse-drag from focused tabs.
// See PickupSection Swiper props: touchStartPreventDefault={false}.
const PICKUP_TABS_SWIPER_FOCUSABLE =
  'input, select, option, textarea, video, label';

interface IPickupFilterTabsRowProps {
  allTabLabel: string;
  filterTabs: PickupFilterTabModel[];
  activeTabId: string;
  onTabChange: (_tabId: string) => void;
  tabPanelId: string;
}

const PickupFilterTabsRow: FC<IPickupFilterTabsRowProps> = ({
  allTabLabel,
  filterTabs,
  activeTabId,
  onTabChange,
  tabPanelId,
}) => {
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const orderedTabs = useMemo(
    () => [
      { id: PICKUP_FILTER_TAB_ALL_ID, label: allTabLabel, stateCodes: [] },
      ...filterTabs,
    ],
    [allTabLabel, filterTabs],
  );

  const focusTabByIndex = useCallback(
    (index: number) => {
      const next = orderedTabs[index];
      if (!next) return;
      onTabChange(next.id);
      tabRefs.current.get(next.id)?.focus();
    },
    [onTabChange, orderedTabs],
  );

  const onTabListKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const currentIndex = orderedTabs.findIndex(t => t.id === activeTabId);
      if (currentIndex < 0) return;

      switch (e.key) {
        case 'ArrowRight': {
          e.preventDefault();
          const next = (currentIndex + 1) % orderedTabs.length;
          focusTabByIndex(next);
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          const next =
            (currentIndex - 1 + orderedTabs.length) % orderedTabs.length;
          focusTabByIndex(next);
          break;
        }
        case 'Home': {
          e.preventDefault();
          focusTabByIndex(0);
          break;
        }
        case 'End': {
          e.preventDefault();
          focusTabByIndex(orderedTabs.length - 1);
          break;
        }
        default:
          break;
      }
    },
    [activeTabId, focusTabByIndex, orderedTabs],
  );

  return (
    <div
      aria-label="Filter locations by state or region"
      className={styles.pickup_filter_tabs}
      onKeyDown={onTabListKeyDown}
      role="tablist"
    >
      <Swiper
        className={styles.pickup_filter_tabs__swiper}
        focusableElements={PICKUP_TABS_SWIPER_FOCUSABLE}
        freeMode
        modules={[FreeMode]}
        slidesPerView="auto"
        spaceBetween={8}
        touchStartPreventDefault={false}
      >
        {orderedTabs.map(tab => {
          const selected = tab.id === activeTabId;
          return (
            <SwiperSlide key={tab.id}>
              <button
                aria-controls={tabPanelId}
                aria-selected={selected}
                className={classNames(styles.pickup_filter_tabs__tab, {
                  [styles.pickup_filter_tabs__tab_selected]: selected,
                })}
                id={`pickup-filter-tab-${tab.id}`}
                onClick={() => onTabChange(tab.id)}
                ref={el => {
                  if (el) tabRefs.current.set(tab.id, el);
                  else tabRefs.current.delete(tab.id);
                }}
                role="tab"
                tabIndex={selected ? 0 : -1}
                type="button"
              >
                {tab.label}
              </button>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

interface IPickupSectionProps {
  showInstructionText: boolean;
  loadingLocation: boolean;
  handleUseCurrentLocation: () => void;
}

const PickupSection: FC<IPickupSectionProps> = ({
  showInstructionText,
  loadingLocation,
  handleUseCurrentLocation,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const {
    isPickup,
    isPickupInvalid,
    setIsPickupInvalid,
    singleStore,
    pickupSectionTitle,
    pickupAllStore,
    showSearchBox,
    showFilterTabs,
    allFilterTabLabel,
    filterTabItems,
    selectedStoreId,
  } = useStoreLocatorContext();

  const handleChange = debounce(e => {
    setSearchTerm(e.target.value);
  }, 300);

  const filterTabModels = useMemo(
    () =>
      buildPickupFilterTabs(filterTabItems, pickupAllStore ?? [], {
        strictPrismic: !!showFilterTabs,
      }),
    [filterTabItems, pickupAllStore, showFilterTabs],
  );

  const showPickupFilterTabs =
    !!showFilterTabs && (pickupAllStore?.length ?? 0) > 1;

  const [activePickupTabId, setActivePickupTabId] = useState(
    PICKUP_FILTER_TAB_ALL_ID,
  );

  useEffect(() => {
    if (!showPickupFilterTabs) return;
    setActivePickupTabId(PICKUP_FILTER_TAB_ALL_ID);
  }, [showPickupFilterTabs]);

  useEffect(() => {
    if (!showPickupFilterTabs) return;
    if (selectedStoreId) return;
    setActivePickupTabId(PICKUP_FILTER_TAB_ALL_ID);
  }, [selectedStoreId, showPickupFilterTabs]);

  const storesAfterTabFilter = useMemo(() => {
    const base = pickupAllStore ?? [];
    if (!showPickupFilterTabs) return base;
    return filterStoresByPickupTab(
      activePickupTabId,
      filterTabModels,
      base,
    ) as TreezAndExternalStore[];
  }, [
    activePickupTabId,
    filterTabModels,
    pickupAllStore,
    showPickupFilterTabs,
  ]);

  const filteredStores = useMemo(() => {
    return (
      storesAfterTabFilter?.filter(store =>
        store?.name?.toLowerCase().includes(searchTerm?.toLowerCase()),
      ) ?? []
    );
  }, [storesAfterTabFilter, searchTerm]);

  const isRegionalTabActive =
    showPickupFilterTabs && activePickupTabId !== PICKUP_FILTER_TAB_ALL_ID;
  const isSearchClear = !searchTerm?.trim();
  const showEmptyRegionTabMessage =
    isRegionalTabActive && isSearchClear && filteredStores.length === 0;

  return (
    <div className={styles.pickup_section}>
      <Button
        ariaLabel={CURRENT_LOCATION_LABEL}
        className={classNames(styles.current_location, {
          [styles.hidden]: singleStore && isPickup,
        })}
        onClick={handleUseCurrentLocation}
      >
        {loadingLocation ? (
          <Spinner className={styles.crosshairs_icon} sm />
        ) : (
          <Icon className={styles.crosshairs_icon} name="crosshairs" />
        )}
        <span>{CURRENT_LOCATION_LABEL}</span>
      </Button>
      {showInstructionText && (
        <p className={styles.pickup_section__title}>{pickupSectionTitle}</p>
      )}
      {showPickupFilterTabs && (
        <PickupFilterTabsRow
          activeTabId={activePickupTabId}
          allTabLabel={allFilterTabLabel ?? 'All'}
          filterTabs={filterTabModels}
          onTabChange={setActivePickupTabId}
          tabPanelId="pickup-store-list-panel"
        />
      )}
      {showSearchBox && (
        <div className={classNames(styles.store_selector__search_box)}>
          <Input
            autoComplete="off"
            className={styles.store_selector__search_box_input}
            isSmall
            name="pickup-searchBox"
            onChange={e => handleChange(e)}
            placeholder="Search"
            type="search"
          />
          <Button
            ariaLabel="Search"
            className={styles.store_selector__search_box_button}
            color="primary"
            onClick={() => {
              setSearchTerm(searchTerm);
            }}
          >
            <IconSvg aria-hidden="true" name="search" />
          </Button>
        </div>
      )}

      <div className={styles.pickup_section__content}>
        {isPickup && isPickupInvalid && (
          <Alert
            error
            isOpen
            toggle={event => {
              event.stopPropagation();
              setIsPickupInvalid(false);
            }}
          >
            Please select a shop.
          </Alert>
        )}
        <StoresList
          ariaLabelledByTabId={
            showPickupFilterTabs
              ? `pickup-filter-tab-${activePickupTabId}`
              : undefined
          }
          loadingLocation={loadingLocation}
          panelId="pickup-store-list-panel"
          searchTerm={searchTerm}
          showEmptyTabMessage={showEmptyRegionTabMessage}
          stores={filteredStores}
        />
      </div>
    </div>
  );
};

export { PickupSection };
